/**
 * The contract page. The body is the contract: who leads it, the four counts, the funding split
 * by contract line, what needs a decision, the cadences and initiatives it carries, and the
 * contract record. The rail beside it is administration, folded shut until somebody wants it.
 *
 * Everything shown is already filtered by the visibility rule server-side; the one number from
 * unconfirmed sources, the unacknowledged-ask count, arrives as count and age only.
 *
 * With `admin` set, the rail cards get a small inline Edit; each save calls back and the page
 * re-reads the view. The skills switch appears for the sponsor only. `chrome="panels"` drops the
 * contract header, the counts and the funding split, which belong to a contract and not to a unit.
 */
import { useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { AskLine, CadenceLine, ContractLineLoad, Fraction, InitiativeOnContract, LinkedLine, ProgramView, RegisterEntryLike, RiskLine, RoleLine, Tone, UnitCadenceCount } from "@trellis/core";
import { PROGRAM_ADMIN_ROLE } from "@trellis/core";
import { cap, fmtDate, fmtDay, fmtDayFull, fmtStamp, plural } from "@trellis/core";
import type { Person } from "./record.js";
import { Link } from "../components/link.js";
import { Picker } from "../components/picker.js";
import { Tip } from "../components/tip.js";

export interface PickerModel {
  version: string;
  name: string;
  roles: { id: string; name: string; purpose: string }[];
  parameters: { id: string; kind: string; description: string; default?: unknown }[];
  profiles: { id: string; name: string; description: string; cadences: string[]; parameterIds: string[] }[];
  cadences: { id: string; name: string; runsBy: string }[];
}

export interface AdminProps {
  model: PickerModel;
  isSponsor: boolean;
  notice?: { text: string; error?: boolean } | undefined;
  onRole: (roleId: string, upn: string) => Promise<void>;
  onAdminAdd?: (upn: string, displayName?: string) => Promise<void>;
  onAdminRemove?: (upn: string) => Promise<void>;
  onRegister: (entries: RegisterEntryLike[]) => Promise<void>;
  onParameters: (parameters: Record<string, unknown>) => Promise<void>;
  onRosterAdd: (upn: string, displayName?: string) => Promise<void>;
  onRosterRemove: (upn: string) => Promise<void>;
  onSkills: (enabled: boolean) => Promise<void>;
  onGap: (description: string) => Promise<void>;
  viewer: string;
  people?: Person[];
  peopleOf?: () => Promise<Person[]>;
  onAssign?: (id: string, to: string) => Promise<void>;
  structureCard?: { workstreams: number; teams: number; missingDeliveryLeads: number; billedWithoutWorkstream: number };
  onGo?: (path: string) => void;
  onLinkAdd?: (kind: string, key: string, integrationId?: string) => Promise<void>;
  onLinkRemove?: (id: string) => Promise<void>;
  instances?: { id: string; displayName: string; kind: string }[];
}

export interface AssignProps {
  viewer: string;
  people?: Person[] | undefined;
  peopleOf?: (() => Promise<Person[]>) | undefined;
  onAssign: (id: string, to: string) => Promise<void>;
  hint?: string | undefined;
  label?: string | undefined;
}


/**
 * What each panel is, said the way a person would say it. A reader who has not read the
 * delivery model should not have to guess what "conformance" or "across the tree" means.
 */
const PANEL_TIPS: Record<string, string> = {
  "Open work by contract line": "Confirmed open work split by the CLIN that funds it, with the people sitting on each line. A workstream on no line is shown as its own row, because nobody can say whether that work is covered.",
  "Open work by workstream": "Confirmed open work on each workstream and everything under it. A count, in name order: the delivery model reports counts and never ranks.",
  "The last ninety days": "What the record did over the window: how much was opened, how much closed, and how much of that closed on the date first accepted. Counts rather than a trend line, because the record is days old.",
  "Needs a decision": "What Trellis cannot settle on its own: a role nobody holds, a unit with no delivery lead, work on no contract line, a credential that stopped working, a date that has passed.",
  "Cadences · what the model says happens": "The recurring reports and meetings this contract's profile calls for, and who is accountable for each. Trellis raises them on their schedule; a person still runs them.",
  "Initiatives on this contract": "Quarterly pushes carrying work on this contract. One initiative can cross several contracts; the counts here are this contract's share.",
  "Contract record": "Modifications, option years and period dates as the contract system holds them. Trellis reads these and models none of them itself.",
  "Across the tree · counts, not a list of every standup": "Cadences that run on a unit rather than on the contract. Counted, never listed: a contract with thirty teams would otherwise be a page of standups. Each rule is counted twice over, because \"missed\" means two things here: how many units are missing it today, and how many periods have been missed in all. The second is counted the way the portfolio counts, over the whole record rather than over the portfolio's thirty days.",
  "Open a workstream or team": "Every unit you can open, in tree order. The line under each name counts that unit and everything below it, so a workstream whose people all sit on its teams does not read as empty.",
  "Cadences · on this unit": "The recurring meetings and written statuses the model expects of this unit, counted over this unit's own periods and nobody else's. The contract's own cadences are on its page.",
  "Customer asks": "What the customer has asked for and how long each has waited. The standard it is measured against is a parameter of the delivery model, per contract, not a guess. The counts in this head are over every ask on the contract; the lines below are the ones you may see the detail of.",
  "Risks · from the record, not from inboxes": "What Trellis can see going wrong, derived from the record alone: work past its date, a role nobody holds, a cadence that did not run. Nothing here comes from reading anyone's mail.",
  "Weekly status · draft": "A status written from the record for the delivery lead to read, edit and send. Trellis never sends it.",
  "Draft ready · weekly status · you send it": "A status written from the record and waiting for you. Trellis never sends it; you do.",
  "Contract events · from the contract system": "Modifications, option years and period dates as the contract system holds them. Trellis reads these and models none of them itself.",
  "Linked · read, never copied": "The projects, spaces and channels this contract is allowed to read. Trellis reads them where they are; nothing is copied into Trellis, and a query is confined to what is linked here.",
  "Model gaps · open": "Somewhere the model asked for something this contract cannot do. Raising one is how the model changes; it is not a complaint that goes nowhere.",
};

/** What each conformance bar measures, so a low bar reads as a fact rather than a grade. */
const BAR_TIPS: Record<string, string> = {
  "Roles": "How many of the roles the model names have somebody in them. One person may hold more than one.",
  "Cadences": "Completed periods where the cadence ran, over periods that were due. A period still running is not counted either way.",
  "Customer standard": "Customer asks acknowledged inside the standard, over asks in the last thirty days.",
  "Knowledge": "Whether the places the model says work should be written down are named for this contract.",
  "Gates": "Whether the decision points the model requires have been recorded.",
};

function PanelHead({ title, colour, children }: { title: string; colour?: string; children?: ComponentChildren }) {
  const what = PANEL_TIPS[title];
  const label = <span class="label" {...(colour ? { style: `color:${colour}` } : {})}>{title}</span>;
  return (
    <div class="panel-head">
      {what ? <Tip what={what}>{label}</Tip> : label}
      {children}
    </div>
  );
}

export function ProgramScreen({ view, admin, assign, chrome = "contract", head, intro }: {
  view: ProgramView;
  admin?: AdminProps | undefined;
  assign?: AssignProps | undefined;
  chrome?: "contract" | "panels";
  /** The page's own heading block, when the caller owns it. Replaces the contract header. */
  head?: ComponentChildren;
  /** Whatever the caller wants above the panels, inside the same column. */
  intro?: ComponentChildren;
}) {
  const who: AssignProps | undefined = assign ?? (admin?.onAssign ? {
    viewer: admin.viewer,
    people: admin.people ?? [],
    onAssign: admin.onAssign,
    ...(admin.peopleOf ? { peopleOf: admin.peopleOf } : {}),
  } : undefined);
  // Cadences with something to say come first; the ones no period has reached yet fold into one line,
  // so the card stays the mock's height on a young program that applies sixteen cadences.
  const liveCadences = view.cadences.filter((c) => c.status !== "not-measured");
  const quietCadences = view.cadences.filter((c) => c.status === "not-measured");
  // Rules and periods both. "2 missed" counting rules beside a portfolio saying 110 counting periods
  // is two true sentences a reader cannot reconcile, so the head says which is which.
  const missedRules = view.cadences.filter((c) => c.status === "missed").length;
  const missedPeriods = view.cadences.reduce((n, c) => n + (c.missedCount ?? 0), 0);
  const contract = chrome === "contract";
  // A unit's lines are that unit's periods; the contract's are the contract's. The title says so
  // rather than leaving the same heading over two different populations.
  const cadenceTitle = contract ? "Cadences · what the model says happens" : "Cadences · on this unit";
  const go = admin?.onGo;
  return (
    <div class={`body${contract ? " contract" : ""}`}>
      <main class="main program">
        {contract ? <ContractHead view={view} {...(go ? { go } : {})} /> : head}
        {admin?.notice && <div class={`notice${admin.notice.error ? " error" : ""}`}>{admin.notice.text}</div>}

        {contract && <CountStrip cells={contractCounts(view)} />}

        {contract && (
          <div class="charts">
            <WindowTile view={view} />
            <ContractLines view={view} {...(go ? { go } : {})} />
          </div>
        )}

        {intro}

        {contract && (
          <div class="deck">
            <Decisions view={view} {...(go ? { go } : {})} />
            <Workstreams view={view} {...(go ? { go } : {})} {...(admin?.structureCard ? { card: admin.structureCard } : {})} />
          </div>
        )}

        <div class="grid2">
          {!contract && <Decisions view={view} {...(go ? { go } : {})} />}
          <section class="panel anchored" id="cadences" tabIndex={-1}>
            {/* A cadence that did not run is reported here, not on the decision list, so the head
                carries the count: a reader should not have to scan the rows to find one. */}
            <PanelHead title={cadenceTitle}>
              {view.cadences.length > 0 && <><span class="grow" /><span class="note dim">{missedRules ? `${plural(missedRules, "rule")} missed ${plural(missedPeriods, "period")}` : "None missed"}</span></>}
            </PanelHead>
            {view.cadences.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No cadences in this profile.</span></div>}
            {liveCadences.map((c) => <Cadence key={c.cadenceId} c={c} />)}
            {quietCadences.length > 0 && (
              <details class="line fold">
                <summary><span class="dot off" /><span class="body"><span class="fold-title">{plural(quietCadences.length, "more cadence")} {quietCadences.length === 1 ? "applies" : "apply"}</span><span class="s">No period due yet</span></span></summary>
                {quietCadences.map((c) => <Cadence key={c.cadenceId} c={c} />)}
              </details>
            )}
          </section>

          {contract && <Initiatives view={view} {...(go ? { go } : {})} />}

          {contract && <Asks view={view} {...(who ? { who } : {})} />}

          {contract && !("canSend" in view && view.canSend === false) && <section class="panel tinted">
            <PanelHead title={view.draft ? "Draft ready · weekly status · you send it" : "Weekly status · draft"} colour="var(--act)" />
            {view.draft ? (
              <>
                <div class="draft-text">{view.draft.summary}</div>
                <div class="draft-meta">
                  <div><span class="mono" style="color:var(--fg)">{view.draft.skillId}</span>{view.draft.skillVersion ? ` v${view.draft.skillVersion}` : ""} · agency <span class="chip">{cap(view.draft.agency)}</span> · ran {fmtStamp(view.draft.ranAt)}</div>
                  {view.draft.read && <div>{view.draft.read}</div>}
                  {view.draft.cites.length > 0 && <div>Cites {view.draft.cites.join(", ")}.</div>}
                </div>
              </>
            ) : (
              <div class="draft-text muted">No draft yet.</div>
            )}
            {view.drafts.length > 0 && (
              <div class="draft-list">
                <div class="label" style="margin-top:14px">Drafts ready · {view.drafts.length}</div>
                {view.drafts.map((d) => (
                  <div class="draft-line" key={d.id ?? `${d.artifactId}-${d.ranAt}`}>
                    {d.id ? <a href={`/app/programs/${encodeURIComponent(view.programId)}/drafts/${encodeURIComponent(d.id)}`}>{d.title}</a> : <span>{d.title}</span>}
                    <span class="muted"> · {fmtStamp(d.ranAt)} · {plural(d.cites, "citation")}</span>
                  </div>
                ))}
              </div>
            )}
          </section>}
        </div>

        {/* On a unit the asks carry a long line each and there is no draft beside them, so
            they take the column rather than leaving half a row empty. */}
        {!contract && <Asks view={view} {...(who ? { who } : {})} />}

        {view.unitsForWeek.length > 0 && go && (
          <section class="panel">
            <PanelHead title="Open a workstream or team">
              <span class="grow" />
              <span class="note dim">This week, that unit and everything under it</span>
            </PanelHead>
            {view.unitsForWeek.map((u) => (
              <button type="button" class="line unit-pick" key={u.id} style={`padding-left:${u.depth * 18}px`}
                onClick={() => go(`/app/programs/${encodeURIComponent(view.programId)}/units/${encodeURIComponent(u.id)}`)}>
                <span class={`dot ${u.missingDeliveryLead || u.unattendedCount ? "watch" : u.openWork ? "navy" : "off"}`} />
                <span class="body">{u.name}
                  <span class="s">{[
                    u.kind === "team" ? "Team" : "Workstream",
                    plural(u.people, "person", "people"),
                    u.openWork ? `${u.openWork} open` : "",
                    u.missingDeliveryLead ? "no delivery lead" : "",
                  ].filter(Boolean).join(" · ")}</span>
                  {u.unattendedItems.length > 0 && (
                    <span class="s watch-text">
                      {plural(u.unattendedItems.length, "item")} nobody has answered ·{" "}
                      {u.unattendedItems.slice(0, 2).map((i) => `${i.owner}, ${plural(i.days, "day")}`).join(" · ")}
                      {u.unattendedItems.length > 2 ? ` · and ${u.unattendedItems.length - 2} more` : ""}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </section>
        )}

        {view.unitCadences.length > 0 && (
          <section class="panel">
            <PanelHead title="Across the tree · counts, not a list of every standup" />
            {view.unitCadences.map((c) => (
              <div class="line" key={c.cadenceId}>
                <span class={`dot ${c.excepted ? "off" : c.missed || c.unfilled ? "watch" : c.ran ? "good" : "off"}`} />
                <span class="body">{c.name}
                  {!c.excepted && <div class="s">{c.ran} of {c.eligible} eligible units ran{c.missed ? ` · ${c.missed} missed` : ""}{c.unfilled ? ` · ${c.unfilled} role unfilled` : ""}</div>}
                  <div class="s">{treePeriods(c)}</div>
                </span>
              </div>
            ))}
          </section>
        )}

        {contract && <ContractRecord view={view} />}

        {/* Linked systems and model gaps are the contract's; the unit view empties both. */}
        {contract && (
          <div class="grid2">
            <Linked view={view} admin={admin} />
            <Gaps view={view} admin={admin} />
          </div>
        )}

        <section class="panel">
          <div class="panel-head">
            <Tip what="How much of what the delivery model asks for this contract is actually doing, over the parts that can be measured. A fraction with no denominator reads as not measured rather than as zero, because the two are not the same thing.">
              <span class="label">Conformance to the Samtek Delivery Model · v{view.header.modelVersion}</span>
            </Tip>
            <span class="grow" /><span class="note dim">{view.header.conformancePercent === undefined ? "Fractions only until the denominators are defined" : `${view.header.conformancePercent}% over what is measured`}</span>
          </div>
          <div class="bars">{view.conformance.map((f) => <Bar key={f.label} f={f} />)}</div>
        </section>
      </main>

      {/* On a unit the roles are that unit's own; the register, the roster and the
          contract's settings are the contract's and stay on its page. */}
      <aside class="rail narrow contract-rail">
        <Roles view={view} admin={admin} />
        {contract && <>
          <Register view={view} admin={admin} />
          <Roster view={view} admin={admin} />
          <ProgramAdmins view={view} admin={admin} />
          {admin && <Skills view={view} admin={admin} />}
          {admin && <DeliveryModel view={view} />}
        </>}
      </aside>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ the contract

/** Breadcrumb, the contract's name, its number and period, and who leads it. */
function ContractHead({ view, go }: { view: ProgramView; go?: (path: string) => void }) {
  const lines = view.contractLines.filter((l) => l.onLine).length;
  const lead = view.roles.find((r) => r.roleId === "delivery-lead")?.holder;
  const mine = view.roles
    .filter((r) => r.holder?.upn === view.viewer && r.roleId !== PROGRAM_ADMIN_ROLE)
    .map((r) => r.name.toLowerCase());
  const sentences = [
    lead ? `${lead.name} is delivery lead.` : "Nobody holds delivery lead.",
    mine.length ? `You are ${joinWords(mine)}.` : "",
    `${plural(view.header.peopleCount, "person", "people")} on the roster${lines ? `, across ${plural(lines, "contract line")}` : ""}.`,
  ].filter(Boolean);
  return (
    <div class="contract-head">
      {go && <p class="note dim"><button class="link" onClick={() => go("/app/programs")}>Programs</button></p>}
      <div class="contract-title">
        <h1 class="h-xl">{view.header.name}</h1>
        <p class="note dim code num">{contractMeta(view).join(" · ") || "No contract record read yet"}</p>
      </div>
      <p class="lede">{sentences.join(" ")}</p>
    </div>
  );
}

/** The contract number and period, as the contract system holds them. Nothing invented. */
function contractMeta(view: ProgramView): string[] {
  const c = view.contract;
  const out: string[] = [];
  if (c.number) out.push(c.number);
  else if (c.corpdashId) out.push(c.corpdashId);
  if (c.popStart && c.popEnd) out.push(`${fmtDayFull(c.popStart)} to ${fmtDayFull(c.popEnd)}`);
  else if (c.popEnd) out.push(`through ${fmtDayFull(c.popEnd)}`);
  else if (c.popStart) out.push(`from ${fmtDayFull(c.popStart)}`);
  return out;
}

export interface CountCell { n: string; t: string; what: string; watch?: boolean }

/** Four counts in one container with a hairline between them. The ones that want attention take a rule, not a fill. */
export function CountStrip({ cells }: { cells: CountCell[] }) {
  return (
    <div class="count-strip">
      {cells.map((x) => (
        <Tip key={x.t} what={x.what}>
          <span class={`count-tile${x.watch ? " watch" : ""}${x.n === "0" || x.n === "—" ? " zero" : ""}`}>
            <b class="num">{x.n}</b><span>{x.t}</span>
          </span>
        </Tip>
      ))}
    </div>
  );
}

function contractCounts(view: ProgramView): CountCell[] {
  const w = view.window;
  const leaderless = view.unitsForWeek.filter((u) => u.missingDeliveryLead).length;
  return [
    { n: String(view.openCount), t: "open", what: "Confirmed work that is not closed." },
    w.closed
      ? { n: `${Math.round((100 * w.closedOnDate) / w.closed)}%`, t: "closed on the accepted date", what: `${w.closedOnDate} of ${plural(w.closed, "item")} closed in ${w.days} days went out on the date first accepted, with the date never moved.` }
      : { n: "—", t: `nothing closed in ${w.days} days`, what: "No closed work in the window, so there is no rate to state." },
    { n: String(view.overdueCount), t: "late", what: "Open work past its date.", watch: view.overdueCount > 0 },
    { n: String(leaderless), t: `${leaderless === 1 ? "unit" : "units"} with no delivery lead`, what: "Units nobody is accountable for. The org chart draws these as a break in the line.", watch: leaderless > 0 },
  ];
}

/**
 * The prototype drew six months of opened against closed. The record is days old, so a line
 * through it would be a claim about confidence this data cannot support (REALISM.md). The
 * counts and the window are what is true.
 */
function WindowTile({ view }: { view: ProgramView }) {
  const w = view.window;
  return (
    <section class="tile">
      <PanelHead title="The last ninety days" />
      {w.opened === 0 && w.closed === 0
        ? <p class="note dim">Nothing opened or closed in the window.</p>
        : (
          <div class="pair">
            <span class="pair-cell"><b class="num">{w.opened}</b><span>opened</span></span>
            <span class="pair-cell"><b class="num">{w.closed}</b><span>closed</span></span>
            <span class="pair-cell"><b class="num">{w.closedOnDate}</b><span>on the accepted date</span></span>
          </div>
        )}
      <p class="note dim tile-foot">Counts over {w.days} days, not a trend: the record is days old and six points drawn as a line would claim more than it knows.</p>
    </section>
  );
}

/** Open work split by the CLIN that funds it. The bar is the funding spine, never a ranking. */
function ContractLines({ view, go }: { view: ProgramView; go?: (path: string) => void }) {
  const lines = view.contractLines;
  const max = Math.max(1, ...lines.map((l) => l.openWork));
  const path = `/app/programs/${encodeURIComponent(view.programId)}/structure`;
  return (
    <section class="tile anchored" id="open-work" tabIndex={-1}>
      <PanelHead title="Open work by contract line" />
      {lines.length === 0
        ? <p class="note dim">No contract lines on the tree yet. CorpDash sends the CLINs.</p>
        : (
          <div class="spine">
            {lines.map((l, i) => {
              const row = (
                <>
                  <b>{[l.key, l.name].filter(Boolean).join(" · ")}</b>
                  <span class="v num">{l.openWork}</span>
                  <span class="track"><span class="fill" style={`width:${Math.round((100 * l.openWork) / max)}%`} /></span>
                </>
              );
              const cls = `spine-row ${l.onLine ? (i % 2 === 0 ? "line-a" : "line-b") : "line-none"}`;
              return go
                ? <button type="button" class={cls} key={l.id} onClick={() => go(path)}>{row}</button>
                : <span class={cls} key={l.id}>{row}</span>;
            })}
          </div>
        )}
      {lines.length > 0 && <p class="note dim tile-foot">{spineFoot(lines)}</p>}
    </section>
  );
}

function spineFoot(lines: ContractLineLoad[]): string {
  const on = lines.filter((l) => l.onLine);
  const off = lines.find((l) => !l.onLine);
  if (on.length === 0) return "No work is on a contract line yet.";
  const staffed = on.map((l) => `${l.people} on ${l.key}`).join(", ");
  return `${cap(staffed)}. ${off ? `${plural(off.workstreams, "workstream")} ${off.workstreams === 1 ? "sits" : "sit"} on no line.` : "Every workstream sits on one of them."}`;
}

/**
 * Open work per workstream, in name order and without a bar. The delivery model reports
 * counts and never ranks, and ordering a bar chart by count is a league table (REALISM.md).
 */
function Workstreams({ view, go, card }: { view: ProgramView; go?: (path: string) => void; card?: AdminProps["structureCard"] }) {
  const rows = view.unitsForWeek.filter((u) => u.depth === 0).slice().sort((a, b) => a.name.localeCompare(b.name));
  const path = `/app/programs/${encodeURIComponent(view.programId)}`;
  return (
    <section class="tile">
      <PanelHead title="Open work by workstream" />
      {rows.length === 0
        ? <p class="note dim">No workstreams yet.</p>
        : (
          <table class="grid">
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>{go
                    ? <button class="link" onClick={() => go(`${path}/units/${encodeURIComponent(u.id)}`)}>{u.name}</button>
                    : u.name}</td>
                  <td class="num right">{u.openWork}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {card && (
        <div class="panel-foot">
          {card.workstreams} workstreams · {card.teams} teams · {card.billedWithoutWorkstream} billed without a workstream
          {go && <> · <button class="link" onClick={() => go(`${path}/structure`)}>Structure</button> · <button class="link" onClick={() => go(`${path}/roster`)}>Roster</button></>}
        </div>
      )}
    </section>
  );
}

/** What Trellis cannot settle on its own, each with the route that settles it. */
function Decisions({ view, go }: { view: ProgramView; go?: (path: string) => void }) {
  return (
    <section class="tile">
      <PanelHead title="Needs a decision" />
      {view.risks.length === 0 && <p class="note dim">Nothing is waiting on a person. Everything the record can check is in order.</p>}
      {view.risks.map((r) => <Decision key={r.id} r={r} {...(go ? { go } : {})} />)}
    </section>
  );
}

/**
 * The title states the finding, so where there is somewhere to settle it the title is what you
 * press. The trailing "Name one" link it replaces sat after the detail, which put the thing to
 * click furthest from the sentence that explained it.
 */
function Decision({ r, go }: { r: RiskLine; go?: (path: string) => void }) {
  const act = r.action && go ? r.action : undefined;
  return (
    <div class={`callout ${r.tone}`}>
      {act && go
        ? <button class="link callout-title" aria-label={`${r.title} — ${act.label}`} onClick={() => go(act.path)}>{r.title}</button>
        : <b>{r.title}</b>}
      <p class="sub">{r.detail}</p>
    </div>
  );
}

/** Quarterly pushes carrying work here. Counts, and the date the push runs to. */
function Initiatives({ view, go }: { view: ProgramView; go?: (path: string) => void }) {
  return (
    <section class="panel">
      <PanelHead title="Initiatives on this contract" />
      {view.initiatives.length === 0
        ? <p class="note dim">No initiative carries work on this contract.</p>
        : (
          <div class="table-scroll">
            <table class="grid">
              <tbody>
                {view.initiatives.map((i: InitiativeOnContract) => (
                  <tr key={i.id}>
                    <td>{go ? <button class="link" onClick={() => go(`/app/initiatives/${encodeURIComponent(i.id)}`)}>{i.name}</button> : i.name}</td>
                    <td class="note dim">{i.total === 0 ? "No work labelled yet" : `${i.open} open of ${plural(i.total, "commitment")}`}</td>
                    <td class="note right">{i.endsOn ? `Through ${fmtDayFull(i.endsOn)}` : "No end date"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  );
}

/**
 * Modifications, option years and period dates, as the contract system holds them.
 *
 * The head counts unverified rows over this whole table, which is the same count the portfolio
 * shows: it used to say "2 unverified" over a table with more than two, because the portfolio
 * counted only the recent ones and the table narrowed nothing.
 */
function ContractRecord({ view }: { view: ProgramView }) {
  const unverified = view.contractEvents.filter((e) => e.state === "unverified").length;
  return (
    <section class="panel anchored" id="contract-record" tabIndex={-1}>
      <PanelHead title="Contract record">
        <span class="grow" />
        <span class={`note ${unverified ? "tone-watch" : "dim"}`}>
          {/* "None unverified", not "all checked": an event can also be corrected or superseded,
              and the Checked column below says so row by row. */}
          {view.contractEvents.length === 0 ? "Nothing read yet" : unverified ? `${unverified} of ${view.contractEvents.length} unverified` : `None of ${view.contractEvents.length} unverified`}
        </span>
      </PanelHead>
      {view.contractEvents.length === 0
        ? <p class="note dim">Nothing read from the contract system yet{view.contract.corpdashId ? ` for ${view.contract.corpdashId}` : ""}.</p>
        : (
          <div class="table-scroll">
            <table class="grid">
              {/* Not "Effect": a modification's effect is a funding or period change, and
                  those stay in the contract system. What Trellis holds is what kind of row
                  it is and whether anybody has checked it against the signed document. */}
              <thead><tr><th>When</th><th>What</th><th>Kind</th><th>Checked</th></tr></thead>
              <tbody>
                {view.contractEvents.map((e) => (
                  <tr key={e.id}>
                    <td class="note dim">{e.date ? fmtDayFull(e.date) : "No date"}</td>
                    <td>{e.title}</td>
                    <td class="note dim">{cap(e.type.replace(/-/g, " "))}</td>
                    <td class={`note tone-${e.tone}`}>{cap(e.state)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <p class="note dim tile-foot">
        From CorpDash. Trellis reads these and models none of them itself.
        {view.contract.url && <> Checking one against the signed document happens there:{" "}
          {/* The contract, not the row: CorpDash addresses a contract and not a modification. */}
          <a class="link" href={view.contract.url} target="_blank" rel="noopener">open this contract in CorpDash</a>.</>}
      </p>
    </section>
  );
}

/**
 * What the customer asked for and how long each has waited. Never a candidate.
 *
 * An ask on no unit is a contract fact, so this is where it lives and the row says so: it used to
 * appear identically on every unit's This week, where it read as that unit's work. Placing one onto a
 * unit is Structure's job, and the head links there.
 *
 * The head counts every ask on the contract and the lines below are only the ones this reader may
 * see the detail of, so the two can differ by design. When they do the panel says so: it used to
 * print "No customer asks on the record" under a head reporting thirty-five unacknowledged, and a
 * sponsor reading that concludes the contract is clean.
 */
function Asks({ view, who }: { view: ProgramView; who?: AssignProps }) {
  const open = view.asks.filter((a) => a.state !== "closed");
  const closed = view.asks.filter((a) => a.state === "closed");
  const shown = [...open, ...closed.slice(0, 3)];
  const unplaced = open.filter((a) => a.onNoUnit).length;
  const { count, pastStandard, oldestDays, standard } = view.unacknowledged;
  const head = count === 0
    ? "None unacknowledged"
    : [`${count} unacknowledged`, pastStandard ? `${pastStandard} past the ${standard} standard` : "", `oldest ${plural(oldestDays, "day")}`].filter(Boolean).join(" · ");
  return (
    <section class="panel anchored" id="asks" tabIndex={-1}>
      <PanelHead title="Customer asks">
        <span class="grow" />
        <span class="note dim">{head}</span>
      </PanelHead>
      {unplaced > 0 && (
        <p class="note dim ask-unplaced">
          {plural(unplaced, "open ask")} {unplaced === 1 ? "is" : "are"} on no unit.{" "}
          <a class="link" href={`/app/programs/${encodeURIComponent(view.programId)}/structure`}>Place them on Structure</a>
        </p>
      )}
      {view.asks.length === 0 && (
        <div class="line">
          <span class={`dot ${view.asksWithheld > 0 ? "watch" : "off"}`} />
          {/* With nothing listed the panel says what it holds, not that the record is empty: it
              cannot see the whole record, and "none" from a page that has been told thirty-five
              are unacknowledged is the sentence that started this. */}
          <span class="body muted">{view.asksWithheld > 0
            ? `${plural(view.asksWithheld, "unacknowledged customer ask")} on this contract. Their detail is not yours to see, so this panel gives the count and the age only.`
            : "No customer ask is waiting on this contract."}</span>
        </div>
      )}
      {shown.map((a) => (
        <div class="line" key={a.id}>
          <span class={`dot ${a.tone}`} />
          <span class="body">
            <span class="ask-head">
              <span>{a.title}</span>
              {a.onNoUnit && <span class="chip">On no unit</span>}
              {!a.acknowledged && !a.ackWithinStandard && <span class="pill bad">Past the {a.ackStandard} standard</span>}
            </span>
            <AskFacts a={a} />
            <div class="s">{[a.from, a.topics?.length ? `touches ${joinWords(a.topics)}` : ""].filter(Boolean).join(" · ")}{askLinkOpen(a)}</div>
            {who && a.state === "waiting" && a.ownerUpn === who.viewer && (
              <AskAssign people={who.people ?? []} {...(who.peopleOf ? { peopleOf: who.peopleOf } : {})} onAssign={(to) => who.onAssign(a.id, to)} {...(who.hint ? { hint: who.hint } : {})} {...(who.label ? { label: who.label } : {})} />
            )}
          </span>
        </div>
      ))}
      <div class="panel-foot">{asksFoot(view)}</div>
    </section>
  );
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

const ASK_STATE_LABEL: Record<AskLine["state"], string> = {
  waiting: "Waiting for an owner", assigned: "Assigned, not yet accepted", open: "Open", closed: "Closed",
};

/**
 * The same facts `detail` used to fuse into one sentence, each labelled and scannable on its
 * own: who asked, where it stands, who holds it, when it is due (or when it closed), and whether
 * the customer has been acknowledged. Nothing here is new; it is `AskLine`'s own fields read apart.
 */
function AskFacts({ a }: { a: AskLine }) {
  const askedWords = a.askedAtStated === false ? `${fmtDate(a.askedAt)}, date not stated by the source` : `asked ${fmtDate(a.askedAt)}`;
  // Matches `askWaitLine` exactly: a standard missed still says so once the customer has been
  // acknowledged, because the breach already happened and the panel used to keep naming it.
  const ackText = a.acknowledged
    ? `Acknowledged in ${a.waited}${a.ackWithinStandard ? "" : ` · past the ${a.ackStandard} standard`}`
    : `Not acknowledged after ${a.waited} · ${a.ackWithinStandard ? `standard is ${a.ackStandard}` : `past the ${a.ackStandard} standard`}`;
  const facts: { k: string; v: ComponentChildren }[] = [
    { k: "Asked by", v: `${a.askedBy}${a.askedByRole ? `, ${a.askedByRole}` : ""} · ${askedWords}` },
    { k: "State", v: ASK_STATE_LABEL[a.state] },
    { k: "Owner", v: a.owner || "Nobody yet" },
    // Waiting carries no due date, same as the fused sentence used to: nobody has accepted it yet,
    // so the date on the row is not a deadline anybody is being held to.
    ...(a.state === "waiting" ? [] : a.state === "closed" && a.closedAt
      ? [{ k: "Closed", v: fmtDay(a.closedAt) }]
      : [{ k: "Due", v: `${fmtDate(a.dueDate)}${a.overdue ? ` (${plural(a.overdueDays ?? 0, "day")} overdue)` : ""}` }]),
    { k: "Acknowledged", v: ackText },
  ];
  return (
    <dl class="item-facts">
      {facts.map((f) => <div key={f.k}><dt>{f.k}</dt><dd>{f.v}</dd></div>)}
    </dl>
  );
}

const ASK_LINK_LABEL: Record<string, string> = { ticket: "Open ticket", page: "Open page" };

/**
 * The linked ticket or page: the plain reference anybody who sees this ask already sees in
 * `detail` ("ticket COMET-9"), clickable when the source that carried the link also carried its
 * own address. A link without a resolvable address still names the system and the key.
 */
function askLinkOpen(a: AskLine) {
  if (!a.link) return null;
  const ref = `${cap(a.link.system)} ${a.link.ref}`;
  if (!a.url) return <> · {ref}</>;
  return <> · {ref} · <a class="src-link" href={a.url} target="_blank" rel="noopener">{ASK_LINK_LABEL[a.link.system] ?? "Open"}</a></>;
}

// ------------------------------------------------------------------------------------------------ rail

function Roles({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [editing, setEditing] = useState(false);
  const people = view.rosterLines.map((p) => ({ upn: p.upn, name: p.name }));
  const listed = view.roles.filter((r) => r.roleId !== PROGRAM_ADMIN_ROLE);
  return (
    // A section rather than a fragment, because "no delivery lead" on the portfolio lands here.
    <section class="anchored" id="roles" tabIndex={-1}>
      {/* A count over exactly the rows below, not the guarantee the head used to assert:
          "every one has a name" sat directly above rows reading "Nobody yet". */}
      <span class="label with-action">Roles · {listed.filter((r) => r.holder).length} of {listed.length} named{admin && <button class="link" onClick={() => setEditing(!editing)}>{editing ? "Done" : "Edit"}</button>}</span>
      {listed.map((r) => editing && admin
        ? <RoleEditor key={r.roleId} role={r} people={people} onSave={admin.onRole} />
        : <div class="line" key={r.roleId}>
            <span class={`dot ${r.gap ? "bad" : "good"}`} />
            <span class="body">{r.name}<div class="s">{r.holder ? r.holder.name : "Nobody yet"}</div></span>
          </div>)}
      {roleNote(view) && <div class="rail-note">{roleNote(view)}</div>}
    </section>
  );
}

function RoleEditor({ role, people, onSave }: { role: RoleLine; people: { upn: string; name: string }[]; onSave: (roleId: string, upn: string) => Promise<void> }) {
  const [upn, setUpn] = useState(role.holder?.upn ?? "");
  const [busy, setBusy] = useState(false);
  const changed = !!upn && upn !== role.holder?.upn;
  const save = async () => { setBusy(true); try { await onSave(role.roleId, upn); } finally { setBusy(false); } };
  return (
    <div class="line">
      <span class={`dot ${role.gap ? "bad" : "good"}`} />
      <span class="body">{role.name}
        <div class="inline-form">
          <Picker label={role.name} options={people.map((p) => ({ value: p.upn, label: p.name, hint: p.upn }))} value={upn} onChange={setUpn} clearable placeholder="Search people…" />
          {changed && <div class="buttons"><button class="btn" disabled={busy} onClick={() => void save()}>Save</button></div>}
        </div>
      </span>
    </div>
  );
}

function ProgramAdmins({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [adding, setAdding] = useState(false);
  const [upn, setUpn] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const admins = view.roles.filter((r) => r.roleId === PROGRAM_ADMIN_ROLE && r.holder);
  const ok = /^[^@\s]+@[^@\s]+$/.test(upn.trim());
  const add = async () => {
    if (!admin?.onAdminAdd) return;
    setBusy(true);
    try { await admin.onAdminAdd(upn.trim().toLowerCase(), name.trim() || undefined); setAdding(false); setUpn(""); setName(""); }
    finally { setBusy(false); }
  };
  return (
    <details class="fold">
      <summary><span class="label"><span class="fold-title">Program admins · {admins.length || "none"}</span></span></summary>
      {admin?.onAdminAdd && <div class="rail-note">Same access as the delivery lead. <button class="link" onClick={() => setAdding(!adding)}>{adding ? "Cancel" : "Add one"}</button></div>}
      {adding && admin?.onAdminAdd && (
        <div class="inline-form">
          <input type="text" value={upn} placeholder="UPN, or name@company" onInput={(e) => setUpn((e.target as HTMLInputElement).value)} />
          <input type="text" value={name} placeholder="Display name, if new" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          <div class="buttons"><button class="btn" disabled={busy || !ok} onClick={() => void add()}>Add</button></div>
        </div>
      )}
      {admins.length === 0 && <div class="s muted">None yet. Any number.</div>}
      {admins.map((r) => (
        <div class="line" key={r.holder!.upn}>
          <span class="dot good" />
          <span class="body">{r.holder!.name}
            {admin?.onAdminRemove && <div class="s"><button class="link" onClick={() => void admin.onAdminRemove!(r.holder!.upn)}>Remove</button></div>}
          </span>
        </div>
      ))}
    </details>
  );
}

function Register({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <span class="label later with-action">Customer register{admin && <button class="link" onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit"}</button>}</span>
      {editing && admin
        ? <RegisterEditor entries={view.customerRegister} onSave={async (e) => { await admin.onRegister(e); setEditing(false); }} />
        : <>
            {view.customerRegister.length === 0 && <div class="line"><span class="dot navy" /><span class="body muted">No customer personnel named yet.</span></div>}
            {view.customerRegister.map((e) => (
              <div class="line" key={e.address}>
                <span class="dot navy" />
                <span class="body">{e.name}<div class="s">{[e.role, e.note].filter(Boolean).join(" · ") || e.address}</div></span>
              </div>
            ))}
          </>}
    </>
  );
}

function RegisterEditor({ entries, onSave }: { entries: RegisterEntryLike[]; onSave: (entries: RegisterEntryLike[]) => Promise<void> }) {
  const blank = { name: "", address: "", role: "", contractorMarker: "" };
  const [rows, setRows] = useState<{ name: string; address: string; role: string; contractorMarker: string }[]>(
    entries.length ? entries.map((e) => ({ name: e.name, address: e.address, role: e.role ?? "", contractorMarker: e.contractorMarker ?? "" })) : [blank]);
  const [busy, setBusy] = useState(false);
  const set = (i: number, k: "name" | "address" | "role" | "contractorMarker", v: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const kept = rows.filter((r) => r.name.trim() && r.address.trim());
  const ok = kept.length === rows.filter((r) => r.name.trim() || r.address.trim()).length;
  // A marker only means anything on a domain line; a named person is the customer or is not.
  const isDomain = (address: string) => { const a = address.trim(); return a.startsWith("@") || (!!a && !a.includes("@")); };
  const save = async () => {
    setBusy(true);
    try {
      await onSave(kept.map((r) => ({
        name: r.name.trim(), address: r.address.trim(),
        ...(r.role.trim() ? { role: r.role.trim() } : {}),
        ...(isDomain(r.address) && r.contractorMarker.trim() ? { contractorMarker: r.contractorMarker.trim() } : {}),
      })));
    } finally { setBusy(false); }
  };
  return (
    <div class="inline-form register-edit">
      {rows.map((r, i) => (
        <div class="fields" key={i}>
          <input type="text" value={r.name} placeholder="Name" onInput={(e) => set(i, "name", (e.target as HTMLInputElement).value)} />
          <input type="text" value={r.address} placeholder="Address or @domain" onInput={(e) => set(i, "address", (e.target as HTMLInputElement).value)} />
          <input type="text" value={r.role} placeholder="Role" onInput={(e) => set(i, "role", (e.target as HTMLInputElement).value)} />
          {isDomain(r.address) && (
            <input type="text" value={r.contractorMarker} placeholder="Contractor marker in the name, e.g. AGY/CTR"
              onInput={(e) => set(i, "contractorMarker", (e.target as HTMLInputElement).value)} />
          )}
          <button class="link" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <div class="s" style="margin:4px 0 0">On a domain line, anyone at that domain is the customer unless their display name contains the marker.</div>
      <div class="buttons">
        <button class="link" onClick={() => setRows([...rows, blank])}>Add a line</button>
        <button class="btn" disabled={busy || !ok} onClick={() => void save()}>Save</button>
      </div>
    </div>
  );
}

function Roster({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [adding, setAdding] = useState(false);
  const [upn, setUpn] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = /^[^@\s]+@[^@\s]+$/.test(upn.trim());
  const add = async () => { if (!admin) return; setBusy(true); try { await admin.onRosterAdd(upn.trim().toLowerCase(), name.trim() || undefined); setAdding(false); setUpn(""); setName(""); } finally { setBusy(false); } };
  return (
    <details class="fold">
      <summary><span class="label"><span class="fold-title">Roster · {plural(view.rosterLines.length, "person", "people")}</span></span></summary>
      {admin && <div class="rail-note"><button class="link" onClick={() => setAdding(!adding)}>{adding ? "Cancel" : "Add a person"}</button></div>}
      {adding && admin && (
        <div class="inline-form">
          <input type="text" value={upn} placeholder="UPN, or name@company" onInput={(e) => setUpn((e.target as HTMLInputElement).value)} />
          <input type="text" value={name} placeholder="Display name, if new" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          <div class="buttons"><button class="btn" disabled={busy || !ok} onClick={() => void add()}>Add</button><span class="muted">Flagged until staffing agrees.</span></div>
        </div>
      )}
      {(() => {
        // Said once, not once per person: when the whole CorpDash staffing roster is from a stale window, one line above the list.
        const staffing = view.rosterLines.filter((p) => p.source !== "manual" && p.source !== "corpdash-hours");
        const allStale = staffing.length > 0 && staffing.every((p) => p.flagged);
        return allStale ? <div class="s muted" style="margin:4px 0 8px">Staffing plan from CorpDash's last window; hours are current.</div> : null;
      })()}
      <RosterList view={view} admin={admin} />
    </details>
  );
}

function Skills({ view, admin }: { view: ProgramView; admin: AdminProps }) {
  const [busy, setBusy] = useState(false);
  const flip = async () => { setBusy(true); try { await admin.onSkills(!view.skillsEnabled); } finally { setBusy(false); } };
  return (
    <>
      <span class="label later">Skills</span>
      <div class="line">
        <span class={`dot ${view.skillsEnabled ? "good" : "off"}`} />
        <span class="body">{view.skillsEnabled ? "On" : "Off"}
          <div class="s">{admin.isSponsor
            ? <button class="link" disabled={busy} onClick={() => void flip()}>{view.skillsEnabled ? "Turn off" : "Turn on"}</button>
            : "Set by the sponsor"}</div>
        </span>
      </div>
    </>
  );
}

/**
 * The version and profile this contract runs on, and the way in to the rest of it. The
 * parameters and the contract-wide cadence switches are a page of their own: a fold in the
 * rail was somewhere you had to already know to look.
 */
function DeliveryModel({ view }: { view: ProgramView }) {
  return (
    <>
      <span class="label later">Delivery model</span>
      <div class="line">
        <span class="dot navy" />
        <span class="body">v{view.header.modelVersion} · {view.header.profileName}
          <div class="s"><Link class="link model-link" href={`/app/programs/${encodeURIComponent(view.programId)}/model`}>Manage the delivery model</Link></div>
        </span>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------------------------ main

function Linked({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [kind, setKind] = useState("teams-team");
  const [key, setKey] = useState("");
  const [instanceId, setInstanceId] = useState(admin?.instances?.[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const kindLabel = (k: string) => k === "jira-project" ? "ticket project" : k === "confluence-space" ? "page space" : k === "teams-team" ? "team chat" : k === "slack-channel" ? "Slack channel" : k.replace(/-/g, " ");
  // What the far side handed back on the last poll, already-seen rows included. "0 this pass" left
  // the reader to guess what zero counted.
  const readUnit = (k: string) => k === "jira-project" ? "ticket" : k === "confluence-space" ? "page" : "message";
  // Neither has a per-link credential: Teams rides the Trellis Entra app, Slack the one workspace
  // bot token, so neither asks for an instance the way a Jira project or Confluence space does.
  const hasInstance = (k: string) => k !== "teams-team" && k !== "slack-channel";
  const add = async () => {
    if (!admin?.onLinkAdd || !key.trim()) return;
    setBusy(true);
    try { await admin.onLinkAdd(kind, key.trim(), hasInstance(kind) ? instanceId || undefined : undefined); setKey(""); }
    finally { setBusy(false); }
  };
  return (
    <section class="panel">
      <PanelHead title="Linked · read, never copied" />
      {view.linked.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No project, space, or team linked.</span></div>}
      {view.linked.map((l: LinkedLine) => (
        <div class="line" key={l.id}>
          <span class={`dot ${l.unreachable ? "bad" : l.note ? "watch" : l.lastPoll ? "good" : "off"}`} />
          <span class="body">{cap(kindLabel(l.kind))} {l.label ?? l.key}
            {/* The key is a GUID for a team. Show the name when there is one and keep the key
                underneath, because the key is what somebody pastes into Graph or a support call. */}
            <div class="s">{[l.label ? l.key : "", l.instance, l.lastPoll ? `polled ${fmtStamp(l.lastPoll)}` : "not polled", typeof l.count === "number" ? `${plural(l.count, readUnit(l.kind))} read` : "", l.unreachable ? "unreachable" : "", l.unreachable && l.detail ? l.detail : ""].filter(Boolean).join(" · ")}
              {admin?.onLinkRemove && <> · <button class="link" onClick={() => void admin.onLinkRemove!(l.id)}>Remove</button></>}
            </div>
            {/* Polled fine and still doing nothing useful. Amber, not red: it is reachable. */}
            {l.note && !l.unreachable && <div class="s tone-watch">{l.note}</div>}
          </span>
        </div>
      ))}
      {admin?.onLinkAdd && (
        <div class="inline-form" style="margin:10px 14px 12px">
          <select value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value)}>
            <option value="teams-team">Team chat</option>
            <option value="jira-project">Ticket project</option>
            <option value="confluence-space">Page space</option>
            <option value="slack-channel">Slack channel</option>
          </select>
          {hasInstance(kind) && <Picker label="Instance" value={instanceId} onChange={setInstanceId} placeholder="Instance…"
            options={(admin.instances ?? []).filter((i) => kind === "jira-project" ? i.kind === "jira" : i.kind === "confluence").map((i) => ({ value: i.id, label: i.displayName }))} />}
          <input value={key} placeholder={kind === "teams-team" ? "Team id" : kind === "slack-channel" ? "Channel id" : "Key"} onInput={(e) => setKey((e.target as HTMLInputElement).value)} />
          <button class="btn" disabled={busy || !key.trim()} onClick={() => void add()}>Add</button>
          {kind === "teams-team" && (
            <p class="note dim" style="flex-basis:100%;margin:8px 0 0">One team, not one channel: every
              channel in it is read, including channels added later. The Trellis app has to be installed
              on that team before it can read anything.</p>
          )}
          {kind === "slack-channel" && (
            <p class="note dim" style="flex-basis:100%;margin:8px 0 0">One channel. The Trellis bot has to
              be invited to it (/invite @trellis) before it can read anything, and the workspace needs a
              bot token set up once for the whole programme.</p>
          )}
        </div>
      )}
    </section>
  );
}

function Gaps({ view, admin }: { view: ProgramView; admin: AdminProps | undefined }) {
  const [raising, setRaising] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const raise = async () => { if (!admin) return; setBusy(true); try { await admin.onGap(text.trim()); setText(""); setRaising(false); } finally { setBusy(false); } };
  if (view.gaps.length === 0 && !admin) return null;
  return (
    <section class="panel">
      <PanelHead title="Model gaps · open"><span class="grow" />{admin && !raising && <button class="link" onClick={() => setRaising(true)}>Raise a gap</button>}</PanelHead>
      {raising && admin && (
        <div class="inline-form">
          <textarea value={text} placeholder="What the model does not say" onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
          <div class="buttons"><button class="btn" disabled={busy || !text.trim()} onClick={() => void raise()}>Raise</button><button class="link" onClick={() => setRaising(false)}>Cancel</button></div>
        </div>
      )}
      {view.gaps.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No open gaps.</span></div>}
      {view.gaps.map((g) => (
        <div class="line" key={g.id}>
          <span class="dot watch" />
          <span class="body">{g.description}<div class="s">{g.raisedBy} · {fmtDate(g.raisedAt)} · model v{view.header.modelVersion}</div></span>
        </div>
      ))}
    </section>
  );
}

/**
 * The same rule counted as periods rather than as units, so this panel and the portfolio's cadence
 * figure can be read against each other. Every field is guarded: an API that has not been
 * redeployed serves the row without them.
 */
function treePeriods(c: UnitCadenceCount): string {
  if (c.excepted) return `Exception in force${c.periodsExcepted ? ` · ${plural(c.periodsExcepted, "recorded period")} not measured` : ""}`;
  const ran = c.periodsRan ?? 0, missed = c.periodsMissed ?? 0, withdrawn = c.periodsWithdrawn ?? 0, off = c.periodsOffUnit ?? 0;
  if (!ran && !missed && !withdrawn) return "No period recorded yet";
  // A withdrawn period was recorded; it is only no longer measured. Leaving it out of the total
  // read "0 periods recorded · 2 withdrawn" on a rule whose periods had all been withdrawn.
  return [
    `${plural(ran + missed + withdrawn, "period")} recorded`,
    `${ran} ran`,
    `${missed} missed`,
    withdrawn ? `${withdrawn} withdrawn` : "",
    off ? `${off} on no unit the rule still applies to` : "",
  ].filter(Boolean).join(" · ");
}

function Cadence({ c }: { c: CadenceLine }) {
  return (
    <div class="line">
      <span class={`dot ${c.tone}`} />
      <span class="body">{c.frequency}: {c.name}<div class="s">{[c.detail, c.holder ? `${c.runsBy}: ${c.holder}` : `${c.runsBy}: nobody yet`].filter(Boolean).join(" · ")}</div></span>
    </div>
  );
}

function Bar({ f }: { f: Fraction }) {
  const pct = f.measured && f.of ? Math.round((100 * (f.n ?? 0)) / f.of) : 0;
  const tone: Tone = f.measured ? f.tone : "off";
  const what = BAR_TIPS[f.label];
  // Spans, not divs: this whole row sits inside the tooltip's button, which takes
  // phrasing content only. The CSS gives them their block layout.
  const body = (
    <>
      <span class="t">{f.label}</span>
      <span class="track" role="img" aria-label={`${f.label}: ${f.text}`}><span class={`fill ${tone}`} style={`width:${f.measured ? pct : 0}%`} /></span>
      <span class="n">{f.text}</span>
    </>
  );
  if (!what) return <div class="bar">{body}</div>;
  return <Tip what={f.measured ? what : `${what} Not measured on this contract yet.`}><span class="bar">{body}</span></Tip>;
}

function roleNote(v: ProgramView): string {
  const named = v.roles.filter((r) => r.roleId !== PROGRAM_ADMIN_ROLE);
  const gaps = named.filter((r) => r.gap).length;
  const holders = new Set(named.filter((r) => r.holder).map((r) => r.holder!.upn)).size;
  const parts: string[] = [];
  if (gaps) parts.push(`${plural(gaps, "role")} with nobody.`);
  if (holders === 1 && v.roles.some((r) => r.holder)) parts.push("One person holds every named role.");
  return parts.join(" ");
}

function asksFoot(v: ProgramView): string {
  const std = v.conformance.find((f) => f.label === "Customer standard");
  // With nothing visible the standard is not a fact about the contract, so the foot does not
  // report one: it used to read "No asks in the last 30 days" under a head counting thirty-five.
  const recent = std?.measured
    ? (std.n === std.of ? "All asks in standard in the last 30 days." : `${std.text} in the last 30 days.`)
    : v.asksWithheld > 0 ? "" : "No asks in the last 30 days.";
  // Only when some lines are shown: with none, the panel says it in place of the list.
  const withheld = v.asks.length > 0 && v.asksWithheld > 0
    ? `${plural(v.asksWithheld, "more unacknowledged ask")} on this contract, counted here and not listed: the detail is not yours to see.`
    : "";
  const standard = v.unacknowledged.pastStandard ? `${plural(v.unacknowledged.pastStandard, "ask")} past ${v.unacknowledged.standard}, as a count and an age.` : "";
  return [recent, standard, withheld].filter(Boolean).join(" ");
}

/** The company's own people first, with hours; subcontractor staff grouped by company behind a count, so a large program stays readable. */
function RosterList({ view, admin }: { view: ProgramView; admin?: AdminProps | undefined }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const roleName = (id: string) => view.roles.find((r) => r.roleId === id)?.name ?? id;
  const line = (p: ProgramView["rosterLines"][number]) => {
    const roles = p.roleIds.map(roleName).join(", ");
    const bits = [
      roles,
      p.source === "manual" ? "added by hand" : "",
      // A lingering row is on the roster on the strength of older hours; saying "0 h"
      // alone would read as a fault rather than as what it is.
      p.source === "corpdash-hours-lingering" ? "no hours in 4 weeks"
        : typeof p.hours === "number" ? `${Math.round(p.hours)} h in 4 weeks`
        : p.source === "corpdash-stale" ? "staffing plan only" : "",
    ].filter(Boolean);
    return (
      <div class="line" key={p.upn}>
        {/* Somebody kept by the longer window is not working now: neutral, not good. */}
        <span class={`dot ${p.flagged ? "off" : p.source === "corpdash-hours-lingering" ? "neutral" : typeof p.hours === "number" ? "good" : "neutral"}`} />
        <span class="body">{p.name}
          {(bits.length || (admin && p.source === "manual")) && (
            <div class="s">{cap(bits.join(" · "))}
              {admin && p.source === "manual" && <>{bits.length ? " · " : ""}<button class="link" onClick={() => void admin.onRosterRemove(p.upn)}>Remove</button></>}
            </div>
          )}
        </span>
      </div>
    );
  };
  const home = view.rosterLines.filter((p) => !p.company || p.company === "home").sort((a, b) => (b.hours ?? -1) - (a.hours ?? -1) || a.name.localeCompare(b.name));
  const external = view.rosterLines.filter((p) => p.company && p.company !== "home");
  const companies = [...new Set(external.map((p) => p.company!))].sort();
  return (
    <>
      {home.map(line)}
      {companies.map((c) => {
        const staff = external.filter((p) => p.company === c).sort((a, b) => (b.hours ?? -1) - (a.hours ?? -1));
        const hours = staff.reduce((n, p) => n + (p.hours ?? 0), 0);
        return (
          <div key={c}>
            <div class="line">
              <span class="dot neutral" />
              <span class="body">{c}
                <div class="s">{plural(staff.length, "person")}{hours ? ` · ${Math.round(hours)} h in 4 weeks` : ""} · <button class="link" onClick={() => setOpen({ ...open, [c]: !open[c] })}>{open[c] ? "Hide" : "Show"}</button></div>
              </span>
            </div>
            {open[c] && staff.map(line)}
          </div>
        );
      })}
    </>
  );
}

export function AskAssign({ people, peopleOf, onAssign, hint, label }: { people: Person[]; peopleOf?: (() => Promise<Person[]>) | undefined; onAssign: (to: string) => Promise<void>; hint?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<Person[]>(people);
  const [to, setTo] = useState(people[0]?.upn ?? "");
  const [busy, setBusy] = useState(false);
  const start = () => {
    setOpen(true);
    if (!list.length && peopleOf) void peopleOf().then(setList);
  };
  if (!open) return <div class="s"><button class="link" onClick={start}>{label ?? "Assign…"}</button></div>;
  return (
    <div class="inline-form">
      {!list.length ? <span class="muted">Reading the roster…</span> : (
        <Picker label="Assign to" options={list.map((p) => ({ value: p.upn, label: p.name, hint: p.upn }))} value={to} onChange={setTo} placeholder="Search people…" />
      )}
      <div class="buttons">
        <button class="btn" disabled={busy || !to} onClick={() => { setBusy(true); void onAssign(to).finally(() => setBusy(false)); }}>Assign</button>
        <button class="link" onClick={() => setOpen(false)}>Cancel</button>
        <span class="muted">{hint ?? "They accept before it enters their record. Not to a unit."}</span>
      </div>
    </div>
  );
}
